import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPublishedPosts } from '../services/blogService';
import Header from '../components/Header';
import '../App.css';

// Agregar estilos para animaciones
const styles = `
  @keyframes loading {
    0% { transform: translateX(-100%); }
    50% { transform: translateX(0%); }
    100% { transform: translateX(100%); }
  }
  
  .blog-card {
    transition: all 0.3s ease;
  }
  
  .blog-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(59, 130, 246, 0.2);
  }
`;

// Inyectar estilos
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

// Hook para scroll to top
const useScrollToTop = () => {
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowButton(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return { showButton, scrollToTop };
};

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(3); // 3 posts por página para probar paginación
  const { showButton, scrollToTop } = useScrollToTop();

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const data = await getPublishedPosts();
      setPosts(data);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(posts.map(p => p.category).filter(Boolean))];
  
  // Filtrado y búsqueda mejorados
  const filteredPosts = posts
    .filter(post => {
      const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
      const matchesSearch = searchTerm === '' || 
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (post.excerpt && post.excerpt.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (post.tags && post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())));
      return matchesCategory && matchesSearch;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.createdAt?.toDate?.() || b.createdAt) - new Date(a.createdAt?.toDate?.() || a.createdAt);
      } else if (sortBy === 'oldest') {
        return new Date(a.createdAt?.toDate?.() || a.createdAt) - new Date(b.createdAt?.toDate?.() || b.createdAt);
      } else if (sortBy === 'title') {
        return a.title.localeCompare(b.title);
      }
      return 0;
    });

  // Paginación
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);

  // Resetear página cuando cambian los filtros
  const handleFilterChange = (newCategory, newSearch, newSort) => {
    if (newCategory !== undefined) setSelectedCategory(newCategory);
    if (newSearch !== undefined) setSearchTerm(newSearch);
    if (newSort !== undefined) setSortBy(newSort);
    setCurrentPage(1); // Resetear a la primera página
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a' }}>
      <Header />
      
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h1 style={{ 
            fontSize: '3rem', 
            color: '#f1f5f9', 
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            📚 Blog de Geopolítica
          </h1>
          <p style={{ color: '#94a3b8', fontSize: '1.2rem', marginBottom: '2rem' }}>
            Análisis, teorías y perspectivas sobre el orden mundial
          </p>
          
          {/* Estadísticas del blog */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '2rem',
            marginBottom: '2rem',
            flexWrap: 'wrap'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6' }}>
                {posts.length}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#64748b' }}>
                Posts Publicados
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#8b5cf6' }}>
                {categories.length - 1}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#64748b' }}>
                Categorías
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>
                {filteredPosts.length}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#64748b' }}>
                Resultados
              </div>
            </div>
          </div>
        </div>

        {/* Barra de búsqueda y filtros */}
        <div style={{
          background: 'rgba(30, 41, 59, 0.8)',
          borderRadius: '1rem',
          border: '1px solid rgba(148, 163, 184, 0.2)',
          padding: '2rem',
          marginBottom: '3rem'
        }}>
          {/* Barra de búsqueda */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ position: 'relative', maxWidth: '500px', margin: '0 auto' }}>
              <input
                type="text"
                placeholder="Buscar posts por título, contenido o tags..."
                value={searchTerm}
                onChange={(e) => handleFilterChange(undefined, e.target.value, undefined)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem 0.75rem 3rem',
                  borderRadius: '0.5rem',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  background: 'rgba(15, 23, 42, 0.8)',
                  color: '#f1f5f9',
                  fontSize: '1rem',
                  outline: 'none'
                }}
              />
              <span style={{
                position: 'absolute',
                left: '1rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#64748b',
                fontSize: '1.2rem'
              }}>
                🔍
              </span>
            </div>
          </div>

          {/* Filtros */}
          <div style={{ 
            display: 'flex', 
            gap: '2rem', 
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            {/* Categorías */}
            {categories.length > 1 && (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ color: '#94a3b8', fontSize: '0.9rem', marginRight: '0.5rem' }}>
                  Categoría:
                </span>
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => handleFilterChange(cat, undefined, undefined)}
                    style={{
                      padding: '0.4rem 1rem',
                      borderRadius: '1.5rem',
                      border: 'none',
                      background: selectedCategory === cat 
                        ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                        : 'rgba(15, 23, 42, 0.8)',
                      color: selectedCategory === cat ? 'white' : '#94a3b8',
                      cursor: 'pointer',
                      fontWeight: selectedCategory === cat ? 'bold' : 'normal',
                      transition: 'all 0.3s ease',
                      fontSize: '0.85rem'
                    }}
                  >
                    {cat === 'all' ? 'Todos' : cat}
                  </button>
                ))}
              </div>
            )}

            {/* Ordenamiento */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                Ordenar:
              </span>
              <select
                value={sortBy}
                onChange={(e) => handleFilterChange(undefined, undefined, e.target.value)}
                style={{
                  padding: '0.4rem 0.8rem',
                  borderRadius: '0.5rem',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  background: 'rgba(15, 23, 42, 0.8)',
                  color: '#f1f5f9',
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
              >
                <option value="newest">Más Recientes</option>
                <option value="oldest">Más Antiguos</option>
                <option value="title">Por Título</option>
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📚</div>
            <p style={{ color: '#94a3b8', fontSize: '1.2rem' }}>Cargando posts...</p>
            <div style={{ 
              width: '100px', 
              height: '4px', 
              background: 'rgba(148, 163, 184, 0.2)', 
              borderRadius: '2px',
              margin: '1rem auto',
              overflow: 'hidden'
            }}>
              <div style={{
                width: '30%',
                height: '100%',
                background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
                borderRadius: '2px',
                animation: 'loading 2s ease-in-out infinite'
              }} />
            </div>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            background: 'rgba(30, 41, 59, 0.8)',
            borderRadius: '1rem',
            border: '1px solid rgba(148, 163, 184, 0.2)'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔍</div>
            <h3 style={{ color: '#f1f5f9', fontSize: '1.5rem', marginBottom: '0.5rem' }}>
              No se encontraron posts
            </h3>
            <p style={{ color: '#94a3b8', fontSize: '1.1rem', marginBottom: '1.5rem' }}>
              {searchTerm ? `No hay resultados para "${searchTerm}"` : 'No hay posts publicados aún.'}
            </p>
            {searchTerm && (
              <button
                onClick={() => {
                  handleFilterChange('all', '', 'newest');
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '2rem'
          }}>
            {currentPosts.map(post => (
              <Link 
                key={post.id} 
                to={`/blog/${post.slug}`}
                style={{ textDecoration: 'none' }}
              >
                <article style={{
                  background: 'rgba(30, 41, 59, 0.8)',
                  borderRadius: '1rem',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  overflow: 'hidden',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.2)';
                }}>
                  {post.featuredImage && (
                    <div style={{
                      width: '100%',
                      height: '200px',
                      overflow: 'hidden',
                      background: 'rgba(15, 23, 42, 0.5)'
                    }}>
                      <img 
                        src={post.featuredImage} 
                        alt={post.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </div>
                  )}
                  
                  <div style={{ padding: '1.5rem' }}>
                    {post.category && (
                      <span style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        background: 'rgba(59, 130, 246, 0.2)',
                        color: '#93c5fd',
                        marginBottom: '1rem',
                        width: 'fit-content'
                      }}>
                        {post.category}
                      </span>
                    )}
                    
                    <h2 style={{ 
                      color: '#f1f5f9', 
                      marginBottom: '0.75rem',
                      fontSize: '1.5rem'
                    }}>
                      {post.title}
                    </h2>
                    
                    {post.excerpt && (
                      <p style={{ 
                        color: '#94a3b8', 
                        marginBottom: '1rem',
                        flex: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {post.excerpt}
                      </p>
                    )}
                    
                    {/* Tiempo de lectura estimado */}
                    {post.content && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                        marginBottom: '1rem'
                      }}>
                        <span style={{ fontSize: '0.8rem' }}>📖</span>
                        <span style={{ color: '#64748b', fontSize: '0.8rem' }}>
                          {Math.max(1, Math.ceil(post.content.split(' ').length / 200))} min lectura
                        </span>
                      </div>
                    )}
                    
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 'auto',
                      paddingTop: '1rem',
                      borderTop: '1px solid rgba(148, 163, 184, 0.1)'
                    }}>
                      <span style={{ color: '#64748b', fontSize: '0.875rem' }}>
                        {formatDate(post.createdAt)}
                      </span>
                      {post.tags && post.tags.length > 0 && (
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {post.tags.slice(0, 2).map((tag, idx) => (
                            <span key={idx} style={{
                              fontSize: '0.75rem',
                              color: '#64748b'
                            }}>
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}

        {/* Paginación */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0.5rem',
            marginTop: '3rem',
            padding: '2rem',
            background: 'rgba(30, 41, 59, 0.8)',
            borderRadius: '1rem',
            border: '1px solid rgba(148, 163, 184, 0.2)'
          }}>
            {/* Botón Anterior */}
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                border: '1px solid rgba(148, 163, 184, 0.3)',
                background: currentPage === 1 ? 'rgba(148, 163, 184, 0.1)' : 'rgba(59, 130, 246, 0.2)',
                color: currentPage === 1 ? '#64748b' : '#93c5fd',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                transition: 'all 0.3s ease'
              }}
            >
              ← Anterior
            </button>

            {/* Números de página */}
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => {
                // Mostrar solo páginas cercanas a la actual
                const showPage = 
                  pageNumber === 1 || 
                  pageNumber === totalPages || 
                  (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2);
                
                if (!showPage) {
                  // Mostrar puntos suspensivos
                  if (pageNumber === currentPage - 3 || pageNumber === currentPage + 3) {
                    return (
                      <span key={pageNumber} style={{ 
                        padding: '0.5rem', 
                        color: '#64748b',
                        fontSize: '0.9rem'
                      }}>
                        ...
                      </span>
                    );
                  }
                  return null;
                }

                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      borderRadius: '0.5rem',
                      border: '1px solid rgba(148, 163, 184, 0.3)',
                      background: currentPage === pageNumber 
                        ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                        : 'rgba(15, 23, 42, 0.8)',
                      color: currentPage === pageNumber ? 'white' : '#cbd5e1',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: currentPage === pageNumber ? 'bold' : 'normal',
                      transition: 'all 0.3s ease',
                      minWidth: '2.5rem'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== pageNumber) {
                        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
                        e.currentTarget.style.color = '#93c5fd';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== pageNumber) {
                        e.currentTarget.style.background = 'rgba(15, 23, 42, 0.8)';
                        e.currentTarget.style.color = '#cbd5e1';
                      }
                    }}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>

            {/* Botón Siguiente */}
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                border: '1px solid rgba(148, 163, 184, 0.3)',
                background: currentPage === totalPages ? 'rgba(148, 163, 184, 0.1)' : 'rgba(59, 130, 246, 0.2)',
                color: currentPage === totalPages ? '#64748b' : '#93c5fd',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                transition: 'all 0.3s ease'
              }}
            >
              Siguiente →
            </button>
          </div>
        )}

        {/* Información de paginación */}
        {filteredPosts.length > 0 && (
          <div style={{
            textAlign: 'center',
            marginTop: '1rem',
            color: '#64748b',
            fontSize: '0.9rem'
          }}>
            Mostrando {indexOfFirstPost + 1}-{Math.min(indexOfLastPost, filteredPosts.length)} de {filteredPosts.length} posts
            {totalPages > 1 && ` • Página ${currentPage} de ${totalPages}`}
          </div>
        )}

        <div style={{ 
          marginTop: '3rem', 
          textAlign: 'center',
          padding: '2rem',
          background: 'rgba(30, 41, 59, 0.5)',
          borderRadius: '1rem',
          border: '1px solid rgba(148, 163, 184, 0.2)'
        }}>
          <Link to="/">
            <button style={{
              padding: '0.75rem 2rem',
              borderRadius: '0.5rem',
              border: 'none',
              background: 'rgba(148, 163, 184, 0.2)',
              color: '#cbd5e1',
              cursor: 'pointer',
              fontSize: '1rem'
            }}>
              ← Volver al Inicio
            </button>
          </Link>
        </div>

        {/* Botón Scroll to Top */}
        {showButton && (
          <button
            onClick={scrollToTop}
            style={{
              position: 'fixed',
              bottom: '2rem',
              right: '2rem',
              width: '3rem',
              height: '3rem',
              borderRadius: '50%',
              border: 'none',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              color: 'white',
              cursor: 'pointer',
              fontSize: '1.2rem',
              zIndex: 1000,
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
            }}
          >
            ↑
          </button>
        )}
      </div>
    </div>
  );
};

export default Blog;
